import React from 'react'
import { LoaderCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useCreateWorkspaceMutation } from '@/graphql/mutations/createWorkspaceMutation.gql.generated'
import { useUpdateCurrentUserMutation } from '@/graphql/mutations/updateCurrentUserMutation.gql.generated'
import { useUpdateWorkspaceMutation } from '@/graphql/mutations/updateWorkspaceMutation.gql.generated'
import { CurrentUserDocument } from '@/graphql/queries/currentUserQuery.gql.generated'
import { resolveActionError } from '@/shared/lib/action-error'
import { useNotificationCenter } from '@/shared/notifications/notification-center'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/shared/ui/sheet'
import { Switch } from '@/shared/ui/switch'

type WorkspaceSummary = {
  id: string
  name: string
}

type WorkspaceSheetProps = {
  mode: 'create' | 'edit'
  onOpenChange: (open: boolean) => void
  open: boolean
  workspace?: WorkspaceSummary | null
}

type WorkspaceMutationDependencies = {
  createWorkspace: ReturnType<typeof useCreateWorkspaceMutation>[0]
  notify: ReturnType<typeof useNotificationCenter>['notify']
  onOpenChange: (open: boolean) => void
  switchToWorkspace: boolean
  t: ReturnType<typeof useTranslation<'workspace'>>['t']
  trimmedName: string
  updateCurrentUser: ReturnType<typeof useUpdateCurrentUserMutation>[0]
}

type WorkspaceRenameDependencies = {
  notify: ReturnType<typeof useNotificationCenter>['notify']
  onOpenChange: (open: boolean) => void
  t: ReturnType<typeof useTranslation<'workspace'>>['t']
  trimmedName: string
  updateWorkspace: ReturnType<typeof useUpdateWorkspaceMutation>[0]
  workspace: WorkspaceSummary | null | undefined
}

function useWorkspaceSheetState({
  mode,
  workspace,
}: Pick<WorkspaceSheetProps, 'mode' | 'workspace'>) {
  const [name, setName] = React.useState(
    mode === 'edit' ? (workspace?.name ?? '') : '',
  )
  const [switchToWorkspace, setSwitchToWorkspace] = React.useState(true)

  return {
    canSubmit:
      name.trim().length > 0 &&
      (mode === 'create' || name.trim() !== (workspace?.name.trim() ?? '')),
    name,
    setName,
    setSwitchToWorkspace,
    switchToWorkspace,
    trimmedName: name.trim(),
  }
}

function WorkspaceSheetBody({
  canSubmit,
  handleSubmit,
  isSubmitting,
  mode,
  name,
  onCancel,
  onNameChange,
  onSwitchChange,
  switchToWorkspace,
}: {
  canSubmit: boolean
  handleSubmit: (event: React.FormEvent<HTMLFormElement>) => Promise<void>
  isSubmitting: boolean
  mode: WorkspaceSheetProps['mode']
  name: string
  onCancel: () => void
  onNameChange: (value: string) => void
  onSwitchChange: (value: boolean) => void
  switchToWorkspace: boolean
}) {
  const { t } = useTranslation('workspace')

  return (
    <form onSubmit={handleSubmit} className="flex h-full flex-col gap-6 px-4">
      <div className="grid gap-3">
        <Label htmlFor="workspace-sheet-name">{t('workspace:nameLabel')}</Label>
        <Input
          id="workspace-sheet-name"
          value={name}
          onChange={(event) => onNameChange(event.target.value)}
          placeholder={t('workspace:namePlaceholder')}
          disabled={isSubmitting}
        />
      </div>
      {mode === 'create' ? (
        <div className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
          <div className="space-y-1">
            <Label htmlFor="workspace-switch-now">
              {t('workspace:switchNow')}
            </Label>
            <p className="text-sm text-muted-foreground">
              {t('workspace:switchNowHelp')}
            </p>
          </div>
          <Switch
            id="workspace-switch-now"
            checked={switchToWorkspace}
            onCheckedChange={onSwitchChange}
            disabled={isSubmitting}
          />
        </div>
      ) : null}
      <SheetFooter className="mt-auto">
        <Button type="submit" disabled={!canSubmit}>
          {isSubmitting ? (
            <LoaderCircle className="size-4 animate-spin" />
          ) : null}
          {mode === 'create' ? t('workspace:createSubmit') : t('common:save')}
        </Button>
        <Button variant="outline" type="button" onClick={onCancel}>
          {t('common:cancel')}
        </Button>
      </SheetFooter>
    </form>
  )
}

function useWorkspaceSheetSubmitAction({
  createWorkspace,
  mode,
  notify,
  onOpenChange,
  switchToWorkspace,
  trimmedName,
  updateCurrentUser,
  updateWorkspace,
  workspace,
}: Omit<WorkspaceMutationDependencies, 't'> &
  Omit<WorkspaceRenameDependencies, 't'> & {
    mode: WorkspaceSheetProps['mode']
  }) {
  const { t } = useTranslation('workspace')

  return async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    event.stopPropagation()

    if (!trimmedName) {
      return
    }

    try {
      if (mode === 'create') {
        await handleWorkspaceCreate({
          createWorkspace,
          notify,
          onOpenChange,
          switchToWorkspace,
          t,
          trimmedName,
          updateCurrentUser,
        })

        return
      }

      await handleWorkspaceRename({
        notify,
        onOpenChange,
        t,
        trimmedName,
        updateWorkspace,
        workspace,
      })
    } catch (error) {
      const actionError = resolveActionError(error, t('common:pleaseTryAgain'))

      notify({
        title:
          mode === 'create'
            ? t('shell:workspaceCreationFailed')
            : t('shell:workspaceUpdateFailed'),
        description: actionError.message,
        variant: 'error',
      })
    }
  }
}

async function handleWorkspaceCreate({
  createWorkspace,
  notify,
  onOpenChange,
  switchToWorkspace,
  t,
  trimmedName,
  updateCurrentUser,
}: WorkspaceMutationDependencies) {
  const result = await createWorkspace({
    variables: {
      input: {
        name: trimmedName,
      },
    },
  })

  const createdWorkspace = result.data?.createWorkspace

  if (!createdWorkspace) {
    throw new Error('Workspace was not created')
  }

  if (switchToWorkspace) {
    await updateCurrentUser({
      variables: {
        input: {
          workspaceId: createdWorkspace.id,
        },
      },
    })
  }

  notify({
    title: t('workspace:createdTitle'),
    description: switchToWorkspace
      ? t('workspace:createdSwitchDescription', { name: createdWorkspace.name })
      : t('workspace:createdListDescription', { name: createdWorkspace.name }),
    variant: 'success',
  })
  onOpenChange(false)

  if (switchToWorkspace) {
    window.location.reload()
  }
}

async function handleWorkspaceRename({
  notify,
  onOpenChange,
  t,
  trimmedName,
  updateWorkspace,
  workspace,
}: WorkspaceRenameDependencies) {
  if (!workspace) {
    throw new Error('Workspace details are missing')
  }

  await updateWorkspace({
    variables: {
      input: {
        id: workspace.id,
        name: trimmedName,
      },
    },
  })

  notify({
    title: t('workspace:updatedTitle'),
    description: t('workspace:updatedDescription', { name: trimmedName }),
    variant: 'success',
  })
  onOpenChange(false)
}

export function WorkspaceSheet({
  mode,
  onOpenChange,
  open,
  workspace,
}: WorkspaceSheetProps) {
  const { t } = useTranslation('workspace')
  const { notify } = useNotificationCenter()
  const [createWorkspace, createResult] = useCreateWorkspaceMutation({
    refetchQueries: [{ query: CurrentUserDocument }],
  })
  const [updateWorkspace, updateResult] = useUpdateWorkspaceMutation({
    refetchQueries: [{ query: CurrentUserDocument }],
  })
  const [updateCurrentUser] = useUpdateCurrentUserMutation()
  const isSubmitting = createResult.loading || updateResult.loading
  const {
    canSubmit,
    name,
    setName,
    setSwitchToWorkspace,
    switchToWorkspace,
    trimmedName,
  } = useWorkspaceSheetState({
    mode,
    workspace,
  })
  const handleSubmit = useWorkspaceSheetSubmitAction({
    createWorkspace,
    mode,
    notify,
    onOpenChange,
    switchToWorkspace,
    trimmedName,
    updateCurrentUser,
    updateWorkspace,
    workspace,
  })

  const title =
    mode === 'create' ? t('workspace:createTitle') : t('workspace:editTitle')
  const description =
    mode === 'create'
      ? t('workspace:createDescription')
      : t('workspace:editDescription')

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription>{description}</SheetDescription>
        </SheetHeader>
        <WorkspaceSheetBody
          canSubmit={canSubmit && !isSubmitting}
          handleSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          mode={mode}
          name={name}
          onCancel={() => onOpenChange(false)}
          onNameChange={setName}
          onSwitchChange={setSwitchToWorkspace}
          switchToWorkspace={switchToWorkspace}
        />
      </SheetContent>
    </Sheet>
  )
}
